import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { GraphQLContactPreference } from '@sola/schema/dotcom/supporters/schema.generated.ts';

/**
 * Account & Transactions is excluded from the summary because users cannot
 * change it from the settings sheet and must call Supporter Care instead.
 */
const excludedCategory = 'Account_Transaction';

const hasNonExcludedCategory = (
  categories: readonly string[] | null | undefined,
): boolean =>
  (categories ?? []).some((category) => category !== excludedCategory);

export const useComponentContactCard = (
  preferences?: GraphQLContactPreference | null,
) => {
  const { t } = useTranslation(['my-settings']);

  const preferencesSummary = useMemo(() => {
    const doNotContact = preferences?.doNotContact ?? false;
    const emailOptedOut = doNotContact || (preferences?.doNotEmail ?? false);
    const textOptedOut = doNotContact || (preferences?.doNotText ?? false);

    const hasEmailPrefs =
      !emailOptedOut &&
      hasNonExcludedCategory(preferences?.emailInterestCategories);
    const hasTextPrefs =
      !textOptedOut &&
      hasNonExcludedCategory(preferences?.textInterestCategories);

    const emailPart = hasEmailPrefs
      ? t('my-settings:contactCard.preferencesSummary.someEmail')
      : t('my-settings:contactCard.preferencesSummary.noEmail');
    const textPart = hasTextPrefs
      ? t('my-settings:contactCard.preferencesSummary.someText')
      : t('my-settings:contactCard.preferencesSummary.noText');

    return `${emailPart}, ${textPart}`;
  }, [preferences, t]);

  return { preferencesSummary };
};
